import React, { useState, useEffect, useContext, useRef } from "react";
import { ServerConfigContext } from "../context/ServerConfigContext";
import { useAuth } from "../context/AuthContext";
import { useJWTAuth } from "../context/JWTAuthContext";
import { formatDateTime } from "../utils/dateFormat";
import RichTextEditor from "../components/RichTextEditor";
import "./PostDetailPage.css";

const PostDetailPage = ({ postId, boardId, onBack = () => {} }) => {
  const serverConfigContext = useContext(ServerConfigContext);
  const { getServer, isConfigLoaded } = serverConfigContext;
  const { isAuthenticated, user } = useAuth();
  const {
    token: jwtToken,
    user: jwtUser,
    isAuthenticated: isJWTAuthenticated,
  } = useJWTAuth();

  // Authorization 헤더 생성 (JWT 토큰 우선 사용)
  const getAuthHeaders = () => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    // JWT 토큰이 있으면 우선 사용, 없으면 기존 AuthContext 토큰 사용
    const authToken = jwtToken || (user && localStorage.getItem("auth_token"));

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
      console.log("🔑 [PostDetailPage] Authorization 헤더 설정:", {
        tokenSource: jwtToken ? "JWT" : "AuthContext",
        tokenLength: authToken.length,
        tokenStart: authToken.substring(0, 20) + "...",
      });
    } else {
      console.warn("⚠️ [PostDetailPage] 인증 토큰이 없습니다.");
    }

    return headers;
  };

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [error, setError] = useState(null);
  // 댓글 작성 관련 상태
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [newCommentContent, setNewCommentContent] = useState("");
  const [newCommentAuthor, setNewCommentAuthor] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  // 댓글 수정/삭제 관련 상태
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState("");
  const [editCommentAuthor, setEditCommentAuthor] = useState("");
  const [commentMenuOpen, setCommentMenuOpen] = useState(null);
  const [commentUpdating, setCommentUpdating] = useState(false);
  // 게시글 수정/삭제 관련 상태
  const [editingPost, setEditingPost] = useState(false);
  const [editPostTitle, setEditPostTitle] = useState("");
  const [editPostContent, setEditPostContent] = useState("");
  const [editPostAuthor, setEditPostAuthor] = useState("");
  const [postUpdating, setPostUpdating] = useState(false);
  // 댓글 답글 작성 관련 상태
  const [replyingToCommentId, setReplyingToCommentId] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [replyAuthor, setReplyAuthor] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);
  // 게시글 좋아요 관련 상태
  const [isPostLiked, setIsPostLiked] = useState(false);
  const [postLiking, setPostLiking] = useState(false);
  // API 중복 호출 방지
  const hasInitialized = useRef(false);

  // 게시글 상세 정보 조회
  const fetchPostDetail = async () => {
    try {
      setLoading(true);
      setError(null);

      const boardServer = getServer("board-api-server");
      if (!boardServer || !boardServer.isActive) {
        throw new Error("게시판 서버에 연결할 수 없습니다.");
      }

      const apiUrl = `/board-api/api/v1/posts/${postId}`;
      console.log("📞 [PostDetailPage] 게시글 상세 API 호출:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(
          `게시글 조회 실패: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.success && result.data) {
        setPost({
          id: result.data.id,
          title: result.data.title,
          content: result.data.content || "",
          author: result.data.author_name || result.data.author || "익명",
          created_at: result.data.created_at,
          views: result.data.views || 0,
          likes: result.data.likes || 0,
          comments_count: result.data.comments_count || 0,
          is_solved: result.data.is_solved || false,
          board_id: result.data.board_id,
        });

        // 게시글 좋아요 상태 설정
        setIsPostLiked(result.data.is_liked || false);
        console.log(
          "✅ [PostDetailPage] 게시글 상세 정보 로드 완료:",
          result.data.title
        );

        // API 응답에 comments가 포함되어 있는 경우 바로 설정
        if (result.data.comments && Array.isArray(result.data.comments)) {
          // 중첩된 comments 구조를 계층적으로 처리
          const processComments = (comments, level = 0) => {
            let processed = [];
            comments.forEach((comment) => {
              processed.push({
                id: comment.id,
                content: comment.content,
                author: comment.author || "익명",
                created_at: comment.created_at,
                likes: comment.like_count || 0,
                level: level,
                has_reply: comment.has_reply || false,
                parent_id: comment.parent_id || null,
                is_liked: comment.is_liked || false,
              });

              // children 배열이 있는 경우 재귀적으로 처리
              if (comment.children && Array.isArray(comment.children)) {
                processed = processed.concat(
                  processComments(comment.children, level + 1)
                );
              }
            });
            return processed;
          };

          const processedComments = processComments(result.data.comments);
          setComments(processedComments);
          console.log(
            "✅ [PostDetailPage] API 응답에서 댓글 로드 완료:",
            processedComments.length,
            "개"
          );
        } else {
          // comments가 포함되지 않은 경우 별도 API 호출
          await fetchComments();
        }

        // 조회수 증가
        await increasePostViews(postId);
      } else {
        throw new Error(result.message || "게시글을 찾을 수 없습니다.");
      }
    } catch (error) {
      console.error("❌ [PostDetailPage] 게시글 상세 조회 실패:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // 댓글 목록 조회
  const fetchComments = async () => {
    try {
      setCommentsLoading(true);

      const boardServer = getServer("board-api-server");
      if (!boardServer || !boardServer.isActive) {
        setComments([]);
        return;
      }

      const apiUrl = `/board-api/api/v1/posts/${postId}/comments`;
      console.log("📞 [PostDetailPage] 댓글 API 호출:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(
          `댓글 조회 실패: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.success && result.data && Array.isArray(result.data)) {
        console.log("🔍 [PostDetailPage] 원본 API 댓글 데이터:", result.data);

        // 중첩된 comments 구조를 계층적으로 처리 (게시글 상세 API와 동일한 방식)
        const processComments = (comments, level = 0) => {
          let processed = [];
          comments.forEach((comment) => {
            processed.push({
              id: comment.id,
              content: comment.content,
              author: comment.author || "익명",
              created_at: comment.created_at,
              likes: comment.like_count || 0,
              level: level,
              has_reply: comment.has_reply || false,
              parent_id: comment.parent_id || null,
              is_liked: comment.is_liked || false,
            });

            // children 배열이 있는 경우 재귀적으로 처리
            if (comment.children && Array.isArray(comment.children)) {
              processed = processed.concat(
                processComments(comment.children, level + 1)
              );
            }
          });
          return processed;
        };

        const processedComments = processComments(result.data);
        console.log(
          "🏗️ [PostDetailPage] 최종 계층 구조 댓글:",
          processedComments
        );
        setComments(processedComments);
        console.log(
          "✅ [PostDetailPage] 댓글 로드 완료:",
          processedComments.length,
          "개"
        );
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error("❌ [PostDetailPage] 댓글 로드 실패:", error);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  // 조회수 증가
  const increasePostViews = async (postId) => {
    try {
      const boardServer = getServer("board-api-server");
      if (!boardServer || !boardServer.isActive) return;

      const apiUrl = `/board-api/api/v1/posts/${postId}/view`;

      // 로그인한 사용자인 경우 Authorization 헤더 포함
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
      };

      // JWT 토큰이 있으면 우선 사용, 없으면 기존 AuthContext 토큰 사용
      const hasAuth = isAuthenticated || isJWTAuthenticated;
      if (hasAuth) {
        const authToken =
          jwtToken || (user && localStorage.getItem("auth_token"));
        if (authToken) {
          headers.Authorization = `Bearer ${authToken}`;
          console.log(
            "🔑 [PostDetailPage] 조회수 증가 - Authorization 헤더 포함"
          );
        }
      }

      await fetch(apiUrl, {
        method: "POST",
        headers: headers,
      });

      console.log(
        "👁️ [PostDetailPage] 조회수 증가:",
        postId,
        hasAuth ? "(인증됨)" : "(비로그인)"
      );
    } catch (error) {
      console.warn("⚠️ [PostDetailPage] 조회수 증가 실패:", error);
    }
  };

  // 게시글 좋아요 토글
  const togglePostLike = async () => {
    const hasAuth = isAuthenticated || isJWTAuthenticated;
    if (!hasAuth) {
      alert("좋아요는 로그인 후 이용 가능합니다.");
      return;
    }

    try {
      setPostLiking(true);

      const boardServer = getServer("board-api-server");
      if (!boardServer || !boardServer.isActive) {
        throw new Error("게시판 서버에 연결할 수 없습니다.");
      }

      const apiUrl = `/board-api/api/v1/posts/${postId}/like`;
      console.log("👍 [PostDetailPage] 게시글 좋아요 API 호출:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(
          `게시글 좋아요 실패: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.success) {
        console.log(
          "✅ [PostDetailPage] 게시글 좋아요 토글 완료:",
          result.data
        );

        // 좋아요 상태와 카운트 업데이트
        const newLikedState = result.data.is_liked;
        const newLikesCount = result.data.likes_count || post.likes;

        setIsPostLiked(newLikedState);
        setPost((prevPost) => ({
          ...prevPost,
          likes: newLikesCount,
        }));

        console.log(
          `${newLikedState ? "👍" : "👎"} [PostDetailPage] 게시글 좋아요 ${
            newLikedState ? "추가" : "제거"
          }, 총 ${newLikesCount}개`
        );
      } else {
        throw new Error(result.message || "게시글 좋아요에 실패했습니다.");
      }
    } catch (error) {
      console.error("❌ [PostDetailPage] 게시글 좋아요 실패:", error);
      alert("좋아요 처리에 실패했습니다: " + error.message);
    } finally {
      setPostLiking(false);
    }
  };

  // 댓글 작성
  const submitComment = async () => {
    const hasAuth = isAuthenticated || isJWTAuthenticated;
    if (!hasAuth) {
      alert("댓글 작성은 로그인 후 이용 가능합니다.");
      return;
    }

    if (!newCommentContent.trim()) {
      alert("댓글 내용을 입력해주세요.");
      return;
    }

    // 로그인된 사용자의 닉네임을 작성자로 사용 (JWT 우선)
    const currentUser = isJWTAuthenticated ? jwtUser : user;
    const authorName = currentUser?.nickname || currentUser?.user_id || "익명";

    try {
      setCommentSubmitting(true);

      const boardServer = getServer("board-api-server");
      if (!boardServer || !boardServer.isActive) {
        throw new Error("게시판 서버에 연결할 수 없습니다.");
      }

      const apiUrl = `/board-api/api/v1/posts/${postId}/comments`;
      console.log("📝 [PostDetailPage] 댓글 작성 API 호출:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          author: authorName,
          content: newCommentContent.trim(),
          parent_id: null, // 새 댓글 (답글이 아님)
        }),
      });

      if (!response.ok) {
        throw new Error(
          `댓글 작성 실패: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.success && result.data) {
        console.log("✅ [PostDetailPage] 댓글 작성 완료:", result.data);

        // 새 댓글을 목록에 추가 (최신 댓글이 마지막에 오도록)
        const newComment = {
          id: result.data.id,
          content: result.data.content,
          author: result.data.author,
          created_at: result.data.created_at,
          likes: 0,
          level: result.data.level || 0,
          has_reply: result.data.has_reply || false,
          parent_id: result.data.parent_id || null,
        };

        setComments((prevComments) => [...prevComments, newComment]);

        // 폼 초기화 및 숨기기
        setNewCommentContent("");
        setShowCommentForm(false);

        // 게시글의 댓글 수 업데이트
        if (post) {
          setPost((prevPost) => ({
            ...prevPost,
            comments_count: (prevPost.comments_count || 0) + 1,
          }));
        }

        alert("댓글이 성공적으로 작성되었습니다!");
      } else {
        throw new Error(result.message || "댓글 작성에 실패했습니다.");
      }
    } catch (error) {
      console.error("❌ [PostDetailPage] 댓글 작성 실패:", error);
      alert("댓글 작성에 실패했습니다: " + error.message);
    } finally {
      setCommentSubmitting(false);
    }
  };

  // 댓글 수정
  const updateComment = async (commentId) => {
    if (!editCommentContent.trim()) {
      alert("댓글 내용을 입력해주세요.");
      return;
    }

    // 원래 댓글의 작성자 정보 가져오기 (변경 불가)
    const originalComment = comments.find((c) => c.id === commentId);
    if (!originalComment) {
      alert("댓글을 찾을 수 없습니다.");
      return;
    }

    try {
      setCommentUpdating(true);

      const boardServer = getServer("board-api-server");
      if (!boardServer || !boardServer.isActive) {
        throw new Error("게시판 서버에 연결할 수 없습니다.");
      }

      const apiUrl = `/board-api/api/v1/comments/${commentId}`;
      console.log("✏️ [PostDetailPage] 댓글 수정 API 호출:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          author: originalComment.author, // 원래 작성자로 고정
          content: editCommentContent.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error(
          `댓글 수정 실패: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.success) {
        console.log("✅ [PostDetailPage] 댓글 수정 완료:", result.data);

        // 댓글 목록에서 해당 댓글 업데이트 (작성자는 변경하지 않음)
        setComments((prevComments) =>
          prevComments.map((comment) =>
            comment.id === commentId
              ? {
                  ...comment,
                  content: editCommentContent.trim(),
                  // 작성자는 원래 그대로 유지 (변경하지 않음)
                  created_at: result.data?.updated_at || comment.created_at,
                }
              : comment
          )
        );

        // 수정 모드 종료
        setEditingCommentId(null);
        setEditCommentContent("");
        setEditCommentAuthor("");
        setCommentMenuOpen(null);

        alert("댓글이 성공적으로 수정되었습니다!");
      } else {
        throw new Error(result.message || "댓글 수정에 실패했습니다.");
      }
    } catch (error) {
      console.error("❌ [PostDetailPage] 댓글 수정 실패:", error);
      alert("댓글 수정에 실패했습니다: " + error.message);
    } finally {
      setCommentUpdating(false);
    }
  };

  // 댓글 삭제
  const deleteComment = async (commentId) => {
    if (!confirm("정말 이 댓글을 삭제하시겠습니까?")) {
      return;
    }

    try {
      const boardServer = getServer("board-api-server");
      if (!boardServer || !boardServer.isActive) {
        throw new Error("게시판 서버에 연결할 수 없습니다.");
      }

      const apiUrl = `/board-api/api/v1/comments/${commentId}`;
      console.log("🗑️ [PostDetailPage] 댓글 삭제 API 호출:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(
          `댓글 삭제 실패: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.success) {
        console.log("✅ [PostDetailPage] 댓글 삭제 완료");

        // 댓글 목록에서 해당 댓글 제거
        setComments((prevComments) =>
          prevComments.filter((comment) => comment.id !== commentId)
        );

        // 게시글의 댓글 수 업데이트
        if (post) {
          setPost((prevPost) => ({
            ...prevPost,
            comments_count: Math.max(0, (prevPost.comments_count || 0) - 1),
          }));
        }

        setCommentMenuOpen(null);
        alert("댓글이 성공적으로 삭제되었습니다!");
      } else {
        throw new Error(result.message || "댓글 삭제에 실패했습니다.");
      }
    } catch (error) {
      console.error("❌ [PostDetailPage] 댓글 삭제 실패:", error);
      alert("댓글 삭제에 실패했습니다: " + error.message);
    }
  };

  // 댓글 수정 시작
  const startEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditCommentContent(comment.content);
    setEditCommentAuthor(comment.author);
    setCommentMenuOpen(null);
  };

  // 댓글 수정 취소
  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditCommentContent("");
    setEditCommentAuthor("");
  };

  // 게시글 수정
  const updatePost = async () => {
    if (
      !editPostTitle.trim() ||
      !editPostContent.trim() ||
      !editPostAuthor.trim()
    ) {
      alert("제목, 작성자, 내용을 모두 입력해주세요.");
      return;
    }

    try {
      setPostUpdating(true);

      const boardServer = getServer("board-api-server");
      if (!boardServer || !boardServer.isActive) {
        throw new Error("게시판 서버에 연결할 수 없습니다.");
      }

      const apiUrl = `/board-api/api/v1/posts/${postId}`;
      console.log("✏️ [PostDetailPage] 게시글 수정 API 호출:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: editPostTitle.trim(),
          author: editPostAuthor.trim(),
          content: editPostContent.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error(
          `게시글 수정 실패: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.success && result.data) {
        console.log("✅ [PostDetailPage] 게시글 수정 완료:", result.data);

        // 게시글 정보 업데이트
        setPost((prevPost) => ({
          ...prevPost,
          title: editPostTitle.trim(),
          author: editPostAuthor.trim(),
          content: editPostContent.trim(),
          created_at: result.data.updated_at || prevPost.created_at,
        }));

        // 수정 모드 종료
        setEditingPost(false);
        setEditPostTitle("");
        setEditPostContent("");
        setEditPostAuthor("");

        alert("게시글이 성공적으로 수정되었습니다!");
      } else {
        throw new Error(result.message || "게시글 수정에 실패했습니다.");
      }
    } catch (error) {
      console.error("❌ [PostDetailPage] 게시글 수정 실패:", error);
      alert("게시글 수정에 실패했습니다: " + error.message);
    } finally {
      setPostUpdating(false);
    }
  };

  // 게시글 삭제
  const deletePost = async () => {
    if (
      !confirm(
        "정말 이 게시글을 삭제하시겠습니까? 삭제된 게시글은 복구할 수 없습니다."
      )
    ) {
      return;
    }

    try {
      const boardServer = getServer("board-api-server");
      if (!boardServer || !boardServer.isActive) {
        throw new Error("게시판 서버에 연결할 수 없습니다.");
      }

      const apiUrl = `/board-api/api/v1/posts/${postId}`;
      console.log("🗑️ [PostDetailPage] 게시글 삭제 API 호출:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(
          `게시글 삭제 실패: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.success) {
        console.log("✅ [PostDetailPage] 게시글 삭제 완료");
        alert("게시글이 성공적으로 삭제되었습니다.");

        // 게시판 목록으로 돌아가기
        onBack();
      } else {
        throw new Error(result.message || "게시글 삭제에 실패했습니다.");
      }
    } catch (error) {
      console.error("❌ [PostDetailPage] 게시글 삭제 실패:", error);
      alert("게시글 삭제에 실패했습니다: " + error.message);
    }
  };

  // 게시글 수정 시작
  const startEditPost = () => {
    setEditPostTitle(post.title);
    setEditPostContent(post.content);
    setEditPostAuthor(post.author);
    setEditingPost(true);
  };

  // 게시글 수정 취소
  const cancelEditPost = () => {
    setEditingPost(false);
    setEditPostTitle("");
    setEditPostContent("");
    setEditPostAuthor("");
  };

  // 댓글 답글 작성
  const submitReply = async (parentCommentId) => {
    const hasAuth = isAuthenticated || isJWTAuthenticated;
    if (!hasAuth) {
      alert("답글 작성은 로그인 후 이용 가능합니다.");
      return;
    }

    if (!replyContent.trim()) {
      alert("답글 내용을 입력해주세요.");
      return;
    }

    // 로그인된 사용자의 닉네임을 작성자로 사용 (JWT 우선)
    const currentUser = isJWTAuthenticated ? jwtUser : user;
    const authorName = currentUser?.nickname || currentUser?.user_id || "익명";

    try {
      setReplySubmitting(true);

      const boardServer = getServer("board-api-server");
      if (!boardServer || !boardServer.isActive) {
        throw new Error("게시판 서버에 연결할 수 없습니다.");
      }

      const apiUrl = `/board-api/api/v1/posts/${postId}/comments`;
      console.log("💬 [PostDetailPage] 답글 작성 API 호출:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          author: authorName,
          content: replyContent.trim(),
          parent_id: parentCommentId, // 상위 댓글 ID
        }),
      });

      if (!response.ok) {
        throw new Error(
          `답글 작성 실패: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.success && result.data) {
        console.log("✅ [PostDetailPage] 답글 작성 완료:", result.data);

        // 부모 댓글의 레벨을 찾아서 +1로 설정
        const parentComment = comments.find((c) => c.id === parentCommentId);
        const parentLevel = parentComment ? parentComment.level : 0;
        const newReplyLevel = Math.min(parentLevel + 1, 5); // 최대 5레벨까지만 허용

        // 새 답글을 목록에 추가 (부모 댓글 바로 아래에)
        const newReply = {
          id: result.data.id,
          content: result.data.content,
          author: result.data.author,
          created_at: result.data.created_at,
          likes: 0,
          level: newReplyLevel,
          has_reply: result.data.has_reply || false,
          parent_id: parentCommentId,
        };

        setComments((prevComments) => {
          // 부모 댓글의 위치를 찾아서 그 바로 아래에 삽입
          const parentIndex = prevComments.findIndex(
            (c) => c.id === parentCommentId
          );
          if (parentIndex === -1) {
            // 부모를 찾을 수 없으면 맨 끝에 추가
            return [...prevComments, newReply];
          }

          // 부모 댓글의 마지막 자식 댓글 위치 찾기
          let insertIndex = parentIndex + 1;
          for (let i = parentIndex + 1; i < prevComments.length; i++) {
            if (prevComments[i].level <= prevComments[parentIndex].level) {
              break;
            }
            insertIndex = i + 1;
          }

          // 새 답글을 적절한 위치에 삽입
          const newComments = [...prevComments];
          newComments.splice(insertIndex, 0, newReply);
          return newComments;
        });

        // 폼 초기화 및 숨기기
        setReplyContent("");
        setReplyingToCommentId(null);

        // 게시글의 댓글 수 업데이트
        if (post) {
          setPost((prevPost) => ({
            ...prevPost,
            comments_count: (prevPost.comments_count || 0) + 1,
          }));
        }

        alert("답글이 성공적으로 작성되었습니다!");
      } else {
        throw new Error(result.message || "답글 작성에 실패했습니다.");
      }
    } catch (error) {
      console.error("❌ [PostDetailPage] 답글 작성 실패:", error);
      alert("답글 작성에 실패했습니다: " + error.message);
    } finally {
      setReplySubmitting(false);
    }
  };

  // 답글 작성 시작
  const startReplyToComment = (commentId) => {
    setReplyingToCommentId(commentId);
    setCommentMenuOpen(null);
    // 다른 폼들 닫기
    setEditingCommentId(null);
    setShowCommentForm(false);
  };

  // 답글 작성 취소
  const cancelReply = () => {
    setReplyingToCommentId(null);
    setReplyContent("");
  };

  // postId가 변경될 때 초기화 상태 리셋
  useEffect(() => {
    hasInitialized.current = false;
    console.log(
      "🔄 [PostDetailPage] postId 변경으로 초기화 상태 리셋:",
      postId
    );
  }, [postId]);

  // 컴포넌트 마운트 시 데이터 로드 (중복 호출 방지)
  useEffect(() => {
    if (isConfigLoaded && postId && !hasInitialized.current) {
      console.log("🔄 [PostDetailPage] 초기화 시작:", {
        isConfigLoaded,
        postId,
        hasInitialized: hasInitialized.current,
      });
      hasInitialized.current = true;
      fetchPostDetail();
    }
  }, [isConfigLoaded, postId]);

  // 컴포넌트 언마운트 시 초기화 상태 리셋
  useEffect(() => {
    return () => {
      console.log("🧹 [PostDetailPage] 컴포넌트 언마운트");
      hasInitialized.current = false;
    };
  }, []);

  // 외부 클릭 시 메뉴 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (commentMenuOpen && !event.target.closest(".comment-menu-container")) {
        setCommentMenuOpen(null);
      }
    };

    if (commentMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [commentMenuOpen]);

  // 로딩 상태
  if (loading) {
    return (
      <div className="post-detail-page">
        <div className="post-detail-header">
          <button className="back-button" onClick={onBack}>
            ← 목록으로
          </button>
        </div>
        <div className="loading-message">
          <div className="loading-spinner"></div>
          <p>게시글을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // 오류 상태
  if (error) {
    return (
      <div className="post-detail-page">
        <div className="post-detail-header">
          <button className="back-button" onClick={onBack}>
            ← 목록으로
          </button>
        </div>
        <div className="error-message">
          <p>⚠️ {error}</p>
          <button className="retry-btn" onClick={fetchPostDetail}>
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  // 게시글이 없는 경우
  if (!post) {
    return (
      <div className="post-detail-page">
        <div className="post-detail-header">
          <button className="back-button" onClick={onBack}>
            ← 목록으로
          </button>
        </div>
        <div className="empty-message">
          <p>📝 게시글을 찾을 수 없습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="post-detail-page">
      {/* 헤더 - 뒤로가기 버튼 */}
      <div className="post-detail-header">
        <button className="back-button" onClick={onBack}>
          ← 목록으로
        </button>
        <div className="header-actions">
          {(() => {
            // JWT 우선으로 현재 사용자 정보 가져오기
            const hasAuth = isAuthenticated || isJWTAuthenticated;
            const currentUser = isJWTAuthenticated ? jwtUser : user;

            if (!hasAuth || !currentUser || !post) {
              return (
                <span className="login-required">
                  로그인 후 수정/삭제가 가능합니다.
                </span>
              );
            }

            // 작성자 확인
            const isAuthor =
              currentUser.nickname === post.author ||
              currentUser.user_id === post.author;

            // 관리자 권한 확인 (admin 또는 moderator)
            const isAdmin =
              currentUser.role === "admin" || currentUser.role === "moderator";

            // 작성자이거나 관리자인 경우 수정/삭제 가능
            const canEdit = isAuthor || isAdmin;

            if (canEdit) {
              return (
                <>
                  <button
                    className="action-btn"
                    onClick={startEditPost}
                    disabled={editingPost || postUpdating}
                  >
                    {editingPost ? "수정 중" : "수정"}
                  </button>
                  <button className="action-btn danger" onClick={deletePost}>
                    삭제
                  </button>
                  {isAdmin && !isAuthor && (
                    <span
                      className="admin-notice"
                      title="관리자 권한으로 수정/삭제"
                    >
                      👑
                    </span>
                  )}
                </>
              );
            } else {
              return null; // 권한이 없으면 아무것도 표시하지 않음
            }
          })()}
        </div>
      </div>

      <div className="post-detail-content">
        {editingPost ? (
          /* 게시글 수정 폼 */
          <div className="post-edit-form">
            <div className="form-header">
              <h2>📝 게시글 수정</h2>
            </div>
            <div className="form-inputs">
              <div className="input-group">
                <label htmlFor="edit-post-title">제목</label>
                <input
                  id="edit-post-title"
                  type="text"
                  value={editPostTitle}
                  onChange={(e) => setEditPostTitle(e.target.value)}
                  placeholder="게시글 제목을 입력하세요"
                  disabled={postUpdating}
                />
              </div>
              <div className="input-group">
                <label htmlFor="edit-post-author">작성자</label>
                <input
                  id="edit-post-author"
                  type="text"
                  value={editPostAuthor}
                  onChange={(e) => setEditPostAuthor(e.target.value)}
                  placeholder="작성자명을 입력하세요"
                  disabled={postUpdating}
                />
              </div>
              <div className="input-group">
                <label htmlFor="edit-post-content">내용</label>
                <RichTextEditor
                  value={editPostContent}
                  onChange={setEditPostContent}
                  placeholder="게시글 내용을 입력하세요..."
                  height="300px"
                  disabled={postUpdating}
                />
              </div>
            </div>
            <div className="form-actions">
              <button
                className="cancel-btn"
                onClick={cancelEditPost}
                disabled={postUpdating}
              >
                취소
              </button>
              <button
                className="submit-btn"
                onClick={updatePost}
                disabled={
                  postUpdating ||
                  !editPostTitle.trim() ||
                  !editPostContent.trim() ||
                  !editPostAuthor.trim()
                }
              >
                {postUpdating ? "수정 중..." : "수정 완료"}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* 1. 제목 영역 */}
            <div className="post-title-section">
              <h1 className="post-title">{post.title}</h1>
              <div className="post-meta">
                <div className="meta-left">
                  <span className="post-author">👤 {post.author}</span>
                  <span className="post-date">
                    📅 {formatDateTime(post.created_at)}
                  </span>
                </div>
                <div className="meta-right">
                  <span className="post-views">👁️ {post.views}</span>
                  <button
                    className={`post-like-btn ${isPostLiked ? "liked" : ""} ${
                      postLiking ? "loading" : ""
                    }`}
                    onClick={togglePostLike}
                    disabled={postLiking}
                    title={isPostLiked ? "좋아요 취소" : "좋아요"}
                  >
                    <span className="like-icon">
                      {isPostLiked ? "❤️" : "🤍"}
                    </span>
                    <span className="like-count">{post.likes}</span>
                  </button>
                  <span className="post-comments">
                    💬 {post.comments_count}
                  </span>
                </div>
              </div>
            </div>

            {/* 2. 내용 영역 */}
            <div className="post-content-section">
              <div
                className="post-content"
                dangerouslySetInnerHTML={{
                  __html:
                    post.content ||
                    '<p class="no-content">내용이 없습니다.</p>',
                }}
              />
            </div>
          </>
        )}

        {/* 3. 댓글 영역 */}
        <div className="comments-section">
          <div className="comments-header">
            <h2 className="comments-title">💬 댓글 ({comments.length})</h2>
            {isAuthenticated || isJWTAuthenticated ? (
              <button
                className="comment-write-btn"
                onClick={() => setShowCommentForm(!showCommentForm)}
              >
                {showCommentForm ? "취소" : "댓글 작성"}
              </button>
            ) : (
              <button
                className="comment-write-btn login-required"
                onClick={() => alert("댓글 작성은 로그인 후 이용 가능합니다.")}
              >
                로그인 후 댓글 작성
              </button>
            )}
          </div>

          {/* 댓글 작성 폼 */}
          {showCommentForm && (
            <div className="comment-form">
              <div className="form-header">
                <h3>💬 새 댓글 작성</h3>
              </div>
              <div className="form-inputs">
                <div className="input-group">
                  <label>작성자</label>
                  <div className="author-display">
                    👤{" "}
                    {(() => {
                      const currentUser = isJWTAuthenticated ? jwtUser : user;
                      return (
                        currentUser?.nickname || currentUser?.user_id || "익명"
                      );
                    })()}
                  </div>
                </div>
                <div className="input-group">
                  <label htmlFor="comment-content">댓글 내용</label>
                  <textarea
                    id="comment-content"
                    value={newCommentContent}
                    onChange={(e) => setNewCommentContent(e.target.value)}
                    placeholder="댓글을 입력하세요..."
                    rows={3}
                    disabled={commentSubmitting}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button
                  className="cancel-btn"
                  onClick={() => {
                    setShowCommentForm(false);
                    setNewCommentContent("");
                  }}
                  disabled={commentSubmitting}
                >
                  취소
                </button>
                <button
                  className="submit-btn"
                  onClick={submitComment}
                  disabled={commentSubmitting || !newCommentContent.trim()}
                >
                  {commentSubmitting ? "등록 중..." : "등록"}
                </button>
              </div>
            </div>
          )}

          {commentsLoading ? (
            <div className="comments-loading">
              <div className="loading-spinner"></div>
              <p>댓글을 불러오는 중...</p>
            </div>
          ) : comments.length === 0 ? (
            <div className="no-comments">
              <p>아직 댓글이 없습니다. 첫 번째 댓글을 작성해보세요!</p>
            </div>
          ) : (
            <div className="comments-list">
              {comments.map((comment, index) => (
                <div
                  key={comment.id}
                  className={`comment-item ${
                    index === 0 ? "first-comment" : ""
                  } ${
                    comment.level > 0 ? "reply-comment" : ""
                  } comment-level-${Math.min(comment.level, 5)}`}
                  style={{ marginLeft: `${Math.min(comment.level, 5) * 30}px` }}
                >
                  {editingCommentId === comment.id ? (
                    // 수정 모드
                    <div className="comment-edit-form">
                      <div className="form-inputs">
                        <div className="input-group">
                          <label>작성자 (변경불가)</label>
                          <div className="author-display">
                            👤 {comment.author}
                          </div>
                        </div>
                        <div className="input-group">
                          <label htmlFor={`edit-content-${comment.id}`}>
                            댓글 내용
                          </label>
                          <textarea
                            id={`edit-content-${comment.id}`}
                            value={editCommentContent}
                            onChange={(e) =>
                              setEditCommentContent(e.target.value)
                            }
                            rows={3}
                            disabled={commentUpdating}
                          />
                        </div>
                      </div>
                      <div className="form-actions">
                        <button
                          className="cancel-btn"
                          onClick={cancelEditComment}
                          disabled={commentUpdating}
                        >
                          취소
                        </button>
                        <button
                          className="submit-btn"
                          onClick={() => updateComment(comment.id)}
                          disabled={
                            commentUpdating || !editCommentContent.trim()
                          }
                        >
                          {commentUpdating ? "수정 중..." : "수정"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    // 일반 모드
                    <>
                      <div className="comment-header">
                        <div className="comment-header-left">
                          {comment.level > 0 && (
                            <span className="reply-indicator">
                              <span className="reply-icon">↳</span>
                            </span>
                          )}
                          <span className="comment-author">
                            👤 {comment.author}
                          </span>
                          <span className="comment-date">
                            📅 {formatDateTime(comment.created_at)}
                          </span>
                          <span className="comment-likes">
                            👍 {comment.likes}
                          </span>
                          {comment.level > 0 && (
                            <span className="comment-level">
                              Lv.{comment.level}
                            </span>
                          )}
                        </div>
                        <div className="comment-header-right">
                          <div className="comment-menu-container">
                            <button
                              className="comment-menu-btn"
                              onClick={() =>
                                setCommentMenuOpen(
                                  commentMenuOpen === comment.id
                                    ? null
                                    : comment.id
                                )
                              }
                            >
                              ⋯
                            </button>
                            {commentMenuOpen === comment.id && (
                              <div className="comment-menu-dropdown">
                                {(() => {
                                  // JWT 우선으로 현재 사용자 정보 가져오기
                                  const hasAuth =
                                    isAuthenticated || isJWTAuthenticated;
                                  const currentUser = isJWTAuthenticated
                                    ? jwtUser
                                    : user;

                                  if (!hasAuth || !currentUser) {
                                    return (
                                      <span className="menu-no-permission">
                                        로그인 후 이용 가능
                                      </span>
                                    );
                                  }

                                  // 작성자 확인
                                  const isAuthor =
                                    currentUser.nickname === comment.author ||
                                    currentUser.user_id === comment.author;

                                  // 관리자 권한 확인 (admin 또는 moderator)
                                  const isAdmin =
                                    currentUser.role === "admin" ||
                                    currentUser.role === "moderator";

                                  // 작성자이거나 관리자인 경우 수정/삭제 가능
                                  const canEdit = isAuthor || isAdmin;

                                  if (canEdit) {
                                    return (
                                      <>
                                        <button
                                          className="menu-item"
                                          onClick={() =>
                                            startEditComment(comment)
                                          }
                                        >
                                          ✏️ 수정
                                          {isAdmin && !isAuthor && (
                                            <span className="admin-badge">
                                              👑
                                            </span>
                                          )}
                                        </button>
                                        <button
                                          className="menu-item delete"
                                          onClick={() =>
                                            deleteComment(comment.id)
                                          }
                                        >
                                          🗑️ 삭제
                                          {isAdmin && !isAuthor && (
                                            <span className="admin-badge">
                                              👑
                                            </span>
                                          )}
                                        </button>
                                      </>
                                    );
                                  } else {
                                    return (
                                      <span className="menu-no-permission">
                                        작성자 또는 관리자만 수정/삭제 가능
                                      </span>
                                    );
                                  }
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="comment-content">
                        {comment.content.split("\n").map((line, lineIndex) => (
                          <p key={lineIndex}>{line || "\u00A0"}</p>
                        ))}
                      </div>
                      <div className="comment-actions">
                        {comment.level < 5 ? (
                          isAuthenticated || isJWTAuthenticated ? (
                            <button
                              className="comment-action-btn"
                              onClick={() => startReplyToComment(comment.id)}
                            >
                              답글
                            </button>
                          ) : (
                            <button
                              className="comment-action-btn login-required"
                              onClick={() =>
                                alert("답글 작성은 로그인 후 이용 가능합니다.")
                              }
                            >
                              로그인 후 답글
                            </button>
                          )
                        ) : (
                          <button
                            className="comment-action-btn disabled"
                            disabled
                            title="최대 답글 깊이에 도달했습니다"
                          >
                            답글 (최대깊이)
                          </button>
                        )}
                        <button className="comment-action-btn">좋아요</button>
                        <button className="comment-action-btn">신고</button>
                        {comment.has_reply && (
                          <span className="has-reply-indicator">
                            💬 답글 있음
                          </span>
                        )}
                      </div>

                      {/* 답글 작성 폼 */}
                      {replyingToCommentId === comment.id && (
                        <div className="reply-form">
                          <div className="form-header">
                            <h4>💬 답글 작성</h4>
                          </div>
                          <div className="form-inputs">
                            <div className="input-group">
                              <label>작성자</label>
                              <div className="author-display">
                                👤{" "}
                                {(() => {
                                  const currentUser = isJWTAuthenticated
                                    ? jwtUser
                                    : user;
                                  return (
                                    currentUser?.nickname ||
                                    currentUser?.user_id ||
                                    "익명"
                                  );
                                })()}
                              </div>
                            </div>
                            <div className="input-group">
                              <label htmlFor={`reply-content-${comment.id}`}>
                                답글 내용
                              </label>
                              <textarea
                                id={`reply-content-${comment.id}`}
                                value={replyContent}
                                onChange={(e) =>
                                  setReplyContent(e.target.value)
                                }
                                placeholder="답글을 입력하세요..."
                                rows={3}
                                disabled={replySubmitting}
                              />
                            </div>
                          </div>
                          <div className="form-actions">
                            <button
                              className="cancel-btn"
                              onClick={cancelReply}
                              disabled={replySubmitting}
                            >
                              취소
                            </button>
                            <button
                              className="submit-btn"
                              onClick={() => submitReply(comment.id)}
                              disabled={replySubmitting || !replyContent.trim()}
                            >
                              {replySubmitting ? "등록 중..." : "등록"}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostDetailPage;
